<template>
  <ActivityHistoryTable
    :title="text.shipping.title"
    :subtitle="text.shipping.subtitle"
    :loading="loading"
    :rows="rows"
    :loading-text="text.shipping.loading"
    :empty-text="text.shipping.empty"
    :headers="text.shipping.table.headers"
    :row-fallback="text.shipping.fallback"
  />
</template>

<script>
import invoicesService from '@/services/invoices';
import { extractCollection } from '@/utils/dataNormalization';
import { formatDateTimeShort } from '@/utils/datePresentation';
import ActivityHistoryTable from '@/components/common/ActivityHistoryTable.vue';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('invoices');

export default {
  name: 'InvoiceDetailTabShipping',
  components: {
    ActivityHistoryTable,
  },
  props: {
    invoice: {
      type: Object,
      default: null,
    },
    refreshTick: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      text,
      loading: false,
      rows: [],
      requestToken: 0,
    };
  },
  computed: {
    invoiceId() {
      return Number(this.invoice?.id || 0);
    },
    invoiceRefreshToken() {
      return String(this.invoice?.last_sent_at || this.invoice?.sent_at || '').trim();
    },
  },
  watch: {
    invoiceId: {
      immediate: true,
      handler() {
        this.loadShipping();
      },
    },
    refreshTick() {
      this.loadShipping();
    },
    invoiceRefreshToken() {
      this.loadShipping();
    },
  },
  methods: {
    async loadShipping() {
      const invoiceId = Number(this.invoiceId || 0);
      if (invoiceId <= 0) {
        this.rows = [];
        return;
      }

      this.loading = true;
      const token = this.requestToken + 1;
      this.requestToken = token;

      try {
        const response = await invoicesService.shippingIndex(invoiceId, { per_page: 200 });
        if (token !== this.requestToken) {
          return;
        }

        this.rows = extractCollection(response)
          .map((row) => this.mapShippingRow(row))
          .filter((row) => row !== null);
      } catch (error) {
        if (token === this.requestToken) {
          this.rows = [];
        }
      } finally {
        if (token === this.requestToken) {
          this.loading = false;
        }
      }
    },
    mapShippingRow(raw) {
      if (!raw || typeof raw !== 'object') {
        return null;
      }

      const statusLabel = String(raw.status_label || raw.status || '').trim();
      const stageLabel = String(raw.stage_label || raw.stage_key || '').trim();
      const eventLabel = [statusLabel, stageLabel].filter((part) => part !== '').join(' · ');
      const dateSource = raw.sent_at || raw.created_at || raw.date_label;
      const fallbackId = [
        String(raw.created_at || raw.sent_at || '').trim(),
        String(raw.subject || '').trim(),
        String(raw.status || '').trim(),
      ].join('|');

      return {
        id: Number(raw.id || 0) || `shipping-${fallbackId}`,
        date_label: formatDateTimeShort(dateSource, { fallback: this.text.shipping.fallback.date }),
        module_label: String(raw.channel_label || this.text.shipping.fallback.module).trim() || this.text.shipping.fallback.module,
        event_label: eventLabel !== '' ? eventLabel : this.text.shipping.fallback.event,
        description_label: String(raw.description_label || raw.subject || this.text.shipping.fallback.description).trim()
          || this.text.shipping.fallback.description,
        actor_label: String(raw.actor_label || raw.requested_by_name || this.text.shipping.fallback.actor).trim()
          || this.text.shipping.fallback.actor,
      };
    },
  },
};
</script>
